import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const AddMemberModal = ({ open, onClose, onAddMember }) => {
  const snackbar = useSnackbarAlert();
  const firstNameRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    channel: "in-store",
    employeePasscode: "",
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (open && firstNameRef.current) {
      setTimeout(() => firstNameRef.current.focus(), 100);
    }
  }, [open]);

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      channel: "in-store",
      employeePasscode: "",
    });
    setFormErrors({});
  };

  const validateForm = async () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name cannot be blank.";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name cannot be blank.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email address.";
    }
    if (formData.phone) {
      if (!/^\d{10}$/.test(formData.phone)) {
        errors.phone = "Phone number must be exactly 10 digits.";
      }
    }

    try {
      const response = await api.post("/admin/employees/validatePasscode", {
        passcode: formData.employeePasscode,
      });
      if (!response.data.valid) {
        errors.employeePasscode = "Invalid employee passcode.";
      }
    } catch (error) {
      console.error("Error validating passcode:", error);
      errors.employeePasscode = "Error validating passcode.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePhoneChange = (value) => {
    const numericValue = value.replace(/\D/g, "");
    if (numericValue.length <= 10) {
      handleChange("phone", numericValue);
    }
    if (!/^\d*$/.test(value)) {
      setFormErrors((prev) => ({
        ...prev,
        phone: "Only numbers are allowed.",
      }));
    } else {
      setFormErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (await validateForm()) {
      try {
        await api.post("/admin/digitalLoyalty", { ...formData });
        snackbar.success("Member added successfully!");
        onAddMember();
        resetForm();
        onClose();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Error adding member. Please try again.";
        snackbar.error(message);
        console.error("Error adding member:", error);
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8 }}
          onClick={handleClose}>
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{ mb: 3, fontWeight: "bold" }}>
          Add New Member
        </Typography>

        <TextField
          inputRef={firstNameRef}
          fullWidth
          label="First Name"
          value={formData.firstName}
          onChange={(e) => handleChange("firstName", e.target.value)}
          error={!!formErrors.firstName}
          helperText={formErrors.firstName}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Last Name"
          value={formData.lastName}
          onChange={(e) => handleChange("lastName", e.target.value)}
          error={!!formErrors.lastName}
          helperText={formErrors.lastName}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          error={!!formErrors.phone}
          helperText={formErrors.phone}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 10 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Channel</InputLabel>
          <Select
            value={formData.channel}
            onChange={(e) => handleChange("channel", e.target.value)}>
            {["in-store", "online"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Employee Passcode"
          type="password"
          value={formData.employeePasscode}
          onChange={(e) => handleChange("employeePasscode", e.target.value)}
          error={!!formErrors.employeePasscode}
          helperText={formErrors.employeePasscode}
          sx={{ mb: 2 }}
        />

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}>
          Confirm
        </Button>
      </Box>
    </Modal>
  );
};

export default AddMemberModal;
